/**
 * Normalize a string by removing accents and diacritics
 * This allows "ca" to match "ça", "etre" to match "être", etc.
 */
export function normalizeString(str) {
  return str
    .normalize('NFD') // Decompose combined characters into base + diacritic
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase()
    .trim();
}

/**
 * Extract the individual words from a text, stripping all punctuation
 * (commas, hyphens, etc.) - not just splitting on whitespace.
 */
export function extractWords(text) {
  if (!text) return [];
  return normalizeString(text).match(/[\p{L}\p{N}]+/gu) || [];
}

/**
 * Check if a word exists as a whole word in text (not as a substring)
 * For example: "loc" will NOT match "clock", but "lock" will match "lock"
 */
export function wordExistsInText(word, text) {
  if (!word || !text) return false;

  const normalizedWord = normalizeString(word);
  return extractWords(text).includes(normalizedWord);
}

const MIN_CLOSE_LENGTH = 4;
const MIN_PREFIX_OVERLAP = 0.6;

/**
 * Levenshtein edit distance between two strings (single-row DP).
 */
export function levenshteinDistance(a, b) {
  const cols = b.length + 1;
  const distances = Array.from({ length: cols }, (_, col) => col);

  for (let row = 1; row <= a.length; row += 1) {
    let previousDiagonal = distances[0];
    distances[0] = row;

    for (let col = 1; col < cols; col += 1) {
      const previousAbove = distances[col];
      const cost = a[row - 1] === b[col - 1] ? 0 : 1;
      distances[col] = Math.min(
        distances[col] + 1, // deletion
        distances[col - 1] + 1, // insertion
        previousDiagonal + cost // substitution
      );
      previousDiagonal = previousAbove;
    }
  }

  return distances[cols - 1];
}

/**
 * Whether two normalized words are close enough to hint at each other without
 * being an exact match — a shared stem ("nation" / "national") or a small
 * typo ("nationnal" / "national").
 */
export function isCloseMatch(a, b) {
  if (!a || !b || a === b) return false;
  if (a.length < MIN_CLOSE_LENGTH || b.length < MIN_CLOSE_LENGTH) return false;

  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (longer.startsWith(shorter) && shorter.length / longer.length >= MIN_PREFIX_OVERLAP) {
    return true;
  }

  const maxDistance = longer.length >= 7 ? 2 : 1;
  return levenshteinDistance(a, b) <= maxDistance;
}

/**
 * Among a set of guessed words, find the closest one to `word` (excluding an
 * exact match), preferring the smallest edit distance when several qualify.
 */
export function findCloseMatch(word, guessedWords) {
  let best = null;
  let bestDistance = Infinity;

  for (const guess of guessedWords) {
    if (guess === word || !isCloseMatch(word, guess)) continue;
    const distance = levenshteinDistance(word, guess);
    if (distance < bestDistance) {
      best = guess;
      bestDistance = distance;
    }
  }

  return best;
}

/**
 * Whether `guess` is close to any word in `text` that hasn't already been
 * found exactly — used to flag a "warm" guess that doesn't land on the nose.
 */
export function hasCloseMatchInText(guess, text, guessedWords) {
  for (const word of extractWords(text)) {
    if (!guessedWords.has(word) && isCloseMatch(guess, word)) return true;
  }
  return false;
}

/**
 * Get a storage key for the daily article progress
 * Format: decrypedia_YYYY-MM-DD_LANG
 */
export function getDailyStorageKey(language = 'en') {
  const today = new Date();
  const dateString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return `decrypedia_${dateString}_${language}`;
}

/**
 * Save game progress to localStorage
 */
export function saveProgress(language, guessedWords, guessCount, articleId, gameWon = false) {
  try {
    const key = getDailyStorageKey(language);
    const data = {
      guessedWords: Array.from(guessedWords),
      guessCount,
      articleId,
      gameWon,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save progress:', error);
  }
}

/**
 * Load game progress from localStorage
 */
export function loadProgress(language, articleId) {
  try {
    const key = getDailyStorageKey(language);
    const stored = localStorage.getItem(key);

    if (!stored) return null;

    const data = JSON.parse(stored);

    // Verify it's for the same article
    if (data.articleId !== articleId) {
      return null;
    }

    return {
      guessedWords: new Set(data.guessedWords),
      guessCount: data.guessCount,
      gameWon: data.gameWon || false
    };
  } catch (error) {
    console.error('Failed to load progress:', error);
    return null;
  }
}

/**
 * Clear old progress entries (keep only last 7 days)
 */
export function cleanupOldProgress() {
  try {
    const keys = Object.keys(localStorage);
    const decrypediaKeys = keys.filter(key => key.startsWith('decrypedia_'));

    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    decrypediaKeys.forEach(key => {
      const parts = key.split('_');
      if (parts.length >= 2) {
        const dateStr = parts[1];
        const date = new Date(dateStr);

        if (date < sevenDaysAgo) {
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Failed to cleanup old progress:', error);
  }
}
